import React, { useEffect, useMemo, useRef, useState } from "react";
import "./TeeSheetTemplate.scss";
import Page from "components/page/Page";
import { useAppDispatch, useAppSelector } from "hooks/redux";
import axios, { CancelToken } from "axios";
import { dequeue, enqueue, showError, showSuccess } from "redux/actions/ui";
import { GetLeague } from "api/rpc/2024-04/facilityAdmin/league/league";
import { GetTournament } from "api/rpc/2024-04/facilityAdmin/tournament/tournament";
import { GetBookingCategory } from "api/rpc/2024-04/facilityAdmin/teesheet/teeSheet";
import {
  ArchiveTeeSheetTemplate,
  DeleteTeeSheetTemplate,
  GetTeeSheetTemplate,
  ITeeSheetTemplate,
  ITeeTimeBookingCategoryUpdate,
  PostDuplicateTemplate,
  PutApplyShotgunTeeSheetTemplate,
  PutRemoveShotgunTeeSheetTemplate,
  PutTeeSheetTemplate,
} from "api/rpc/2024-04/facilityAdmin/teesheet/teesheetTemplate";
import { useHistory, useParams } from "react-router";
import { StatusCode } from "api/protocols";
import { useTranslation } from "react-i18next";
import { IBookingCategory, ITeeTime } from "redux/reducers/models/teetime";
import { ILeague } from "redux/reducers/models/league";
import { ITournament } from "redux/reducers/models/tournament";
import ReactDOM from "react-dom";
import { IDivision } from "redux/reducers/models/facility";
import Card from "components/card/Card";
import FormLayout from "components/form/FormLayout";
import Input from "components/form/input/Input";
import TextField from "components/form/textField/TextField";
import { ButtonNew as Button } from "components/buttonNew";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faArrowUTurnUpLeft,
  faCheck,
  faClock,
  faCreditCardFront,
  faDollarSign,
  faPen,
  faPlus,
  faTrophy,
  faUsers,
  faX,
  faXmark,
  IconName,
} from "@fortawesome/pro-regular-svg-icons";
import DataTable from "components/dataTable/DataTable";
import Tabs from "components/tabs/Tabs";
import Checkbox from "components/form/checkbox/Checkbox";
import { capitalize, convertHoles, convertTime, isEmpty } from "helpers/Helpers";
import { Badge } from "components/badge/Badge";
import useKeyPress from "hooks/events/useKeyPress";
import { isEqualWith, range } from "lodash";
import moment from "moment";
import Popover from "components/popover/Popover";
import TimePick from "components/timePick/TimePick";
import useOutsideAlerter from "hooks/useOutsideAlerter/useOutsideAlerter";
import useModal from "hooks/modals/useModal";
import Portal from "elements/Portal";
import Popup from "components/popup/Popup";
import { GetShotGunTemplate } from "api/rpc/2024-04/facilityAdmin/teesheet/template";
import EditTeeTimeDrawer, { ITeeTimeSettingsState } from "./templateDrawers/EditTeeTimeDrawer";
import ShotgunDrawer, { ITeeTimeSettingShotgunState } from "./templateDrawers/ShotgunDrawer";
import { OverlayTrigger, Tooltip } from "react-bootstrap";

interface ISettingsState {
  selectedTeetimes: number[];
  drawerOpen: boolean;
  startTime: string;
  endTime: string;
}

interface IShotgunSettings {
  drawerOpen: boolean;
}

interface IShotgunGroup {
  startTime: string;
  endTime: string;
  applicationId: number;
  teetimes: Array<ITeeTime>;
}

type TRenderItem = { type: "teetime"; data: ITeeTime } | { type: "shotgun"; data: IShotgunGroup };

export default function TeeSheetTemplate() {
  const dispatch = useAppDispatch();
  const { facilityStore } = useAppSelector(store => store);
  const { templateId } = useParams<{ templateId: string }>();

  const { t } = useTranslation();
  const history = useHistory();
  const shiftPressed = useKeyPress(["ShiftLeft", "ShiftRight"]);

  // Modals
  const {
    state: unsavedModal,
    updateModal: updateUnsavedModal,
    closeModal: closeUnsavedModal,
  } = useModal<{ location: any }>({ location: null });
  const {
    state: deleteShotgunModal,
    updateModal: updateDeleteShotgunModal,
    closeModal: closeDeleteShotgunModal,
  } = useModal<{ teetime: TRenderItem }>({ teetime: null });
  const { state: duplicateModal, updateModal: updateDuplicateModal, closeModal: closeDuplicateModal } = useModal();
  const { state: archiveModal, updateModal: updateArchiveModal, closeModal: closeArchiveModal } = useModal();
  const { state: deleteModal, updateModal: updateDeleteModal, closeModal: closeDeleteModal } = useModal();

  //Time filter
  const [timeFilter, setTimeFilter] = useState<{ open: boolean; startTime: string; endTime: string }>({
    open: false,
    startTime: "",
    endTime: "",
  });
  const [filterTimes, setFilterTimes] = useState<{ startTime: string; endTime: string }>({
    startTime: "",
    endTime: "",
  });
  const timeFilterRef = useRef(null);
  const unblockRef = useRef(null);
  useOutsideAlerter(timeFilterRef, () =>
    setTimeFilter({
      open: false,
      startTime: "",
      endTime: "",
    }),
  );

  const [selectedTab, setSelectedTab] = useState<number>(0);

  // Data required
  const [bookingCategories, setBookingCategories] = useState<Array<IBookingCategory>>([]);
  const [divisions, setDivisions] = useState<Array<IDivision>>([]);

  // Template state
  const [templateState, setTemplateState] = useState<ITeeSheetTemplate>(null);
  const [templateBeforeState, setTemplateBeforeState] = useState<ITeeSheetTemplate>(null);
  // Check if the template was saved at least once (to increment the version when leaving the page)
  const [saveMade, setSaveMade] = useState<boolean>(false);

  // Edit the template's name or description
  const [editTemplateState, setEditTemplateState] = useState<{ name: string; description: string; edit: boolean }>({
    name: "",
    description: "",
    edit: false,
  });

  const [renderList, setRenderList] = useState<Array<TRenderItem>>([]);
  const [renderListBeforeState, setRenderListBeforeState] = useState<Array<TRenderItem>>([]);

  const [settingsState, setSettingsState] = useState<ISettingsState>({
    selectedTeetimes: [],

    drawerOpen: false,
    startTime: "",
    endTime: "",
  });

  // Map for quick access to tee time data
  const [teeTimesMap, setTeeTimesMap] = useState<{ [key: number]: ITeeTime }>({});

  // Shotgun drawer
  const [shotgunSettingsState, setShotgunSettingsState] = useState<IShotgunSettings>({
    drawerOpen: false,
  });

  const divisionOptions = [
    {
      label: `${convertHoles(
        9,
        facilityStore.facility?.courses?.find(course => course?.id === templateState?.course_id),
      )} ${t("Holes")}`,
      value: 1,
    },
    {
      label: `${convertHoles(
        18,
        facilityStore.facility?.courses?.find(course => course?.id === templateState?.course_id),
      )} ${t("Holes")}`,
      value: 2,
    },
  ];

  const unsavedChangesExist = useMemo(() => {
    return !isEqualWith(templateBeforeState, templateState);
  }, [templateBeforeState, templateState]);

  useEffect(() => {
    if (unblockRef.current) {
      //clear old blocker
      unblockRef.current();
    }

    unblockRef.current = history.block(nextLocation => {
      if (unsavedChangesExist) {
        updateUnsavedModal({
          isOpen: true,
          location: nextLocation,
        });
        return false;
      }

      // Increment version when leaving the page and at least one 'save' was made to update the template
      if (saveMade) {
        void incrementVersion();
      }

      return;
    });

    return () => {
      unblockRef.current?.();
    };
  }, [history, unsavedChangesExist]);

  useEffect(() => {
    const source = axios.CancelToken.source();
    if (facilityStore.facility) {
      void loadTemplatePage(source.token);
    }
    return () => source.cancel();
  }, [facilityStore.facility]);

  // Tee times split by division, for "All" tab
  const filteredTeeTimes = useMemo(() => {
    if (templateState?.tee_times && divisions) {
      const shotgunMap = buildShotgunGroups(templateState?.tee_times);
      const filteredTimes: Array<Array<TRenderItem>> = [];

      divisions?.forEach(division => {
        const teeTimesByDivision: Array<TRenderItem> = buildRenderList(
          templateState?.tee_times?.filter(teetime => teetime.division_id === division.id),
          shotgunMap,
          division?.id,
        );

        filteredTimes.push(teeTimesByDivision);
      });
      return filteredTimes;
    } else {
      return [];
    }
  }, [templateState?.tee_times, divisions]);

  // Helper for building shotgun groups
  function buildShotgunGroups(teeTimes: ITeeTime[]): Map<number, IShotgunGroup> {
    const groups: IShotgunGroup[] = [];

    teeTimes.forEach(teetime => {
      if (
        teetime?.shotgun_application_id &&
        teetime?.blocked_type !== "shotgun_blocked" &&
        teetime?.blocked_type !== "shotgun_crossover_blocked"
      ) {
        const index = groups.findIndex(g => g.applicationId === teetime.shotgun_application_id);

        if (index !== -1) {
          const group = groups[index];

          const newStart = moment(teetime.start_time, "HH:mm").isBefore(moment(group.startTime, "HH:mm"))
            ? teetime.start_time
            : group.startTime;

          const newEnd = moment(teetime.start_time, "HH:mm").isAfter(moment(group.endTime, "HH:mm"))
            ? teetime.start_time
            : group.endTime;

          groups[index] = {
            ...group,
            startTime: newStart,
            endTime: newEnd,
            teetimes: [...group.teetimes, teetime],
          };
        } else {
          groups.push({
            applicationId: teetime.shotgun_application_id,
            teetimes: [teetime],
            startTime: teetime.start_time,
            endTime: teetime.start_time,
          });
        }
      }
    });

    return new Map(groups.map(g => [g.applicationId, g]));
  }

  // Helper for building out the render list (tee times & shotgun blocks)
  function buildRenderList(
    teeTimes: ITeeTime[],
    shotgunMap: Map<number, IShotgunGroup>,
    divisionId?: number,
  ): TRenderItem[] {
    const renderList: TRenderItem[] = [];
    const seen = new Set<number>();

    teeTimes.forEach(teetime => {
      const appId = teetime.shotgun_application_id;

      if (
        appId &&
        shotgunMap.has(appId) &&
        teetime.blocked_type !== "shotgun_blocked" &&
        teetime.blocked_type !== "shotgun_crossover_blocked"
      ) {
        if (!seen.has(appId)) {
          renderList.push({
            type: "shotgun",
            data: {
              ...shotgunMap.get(appId),
              teetimes: shotgunMap
                .get(appId)
                ?.teetimes.filter(
                  t =>
                    t.blocked_type !== "shotgun_disabled" &&
                    t.blocked_type !== "shotgun_crossover_disabled" &&
                    (!divisionId || t.division_id === divisionId),
                ),
            },
          });

          seen.add(appId);
        }
        return;
      }

      renderList.push({
        type: "teetime",
        data: teetime,
      });
    });

    return renderList;
  }

  // Helper for determining if the tee time is within a time range
  function isWithinTimeRange(time: string, start?: string, end?: string) {
    const m = moment(time, "HH:mm");

    return (!start || m.isSameOrAfter(moment(start, "HH:mm"))) && (!end || m.isSameOrBefore(moment(end, "HH:mm")));
  }

  function applyTimeRange() {
    let updatedSelectedTeeTimes = settingsState?.selectedTeetimes ? [...settingsState.selectedTeetimes] : [];

    const selectTeeTimeIds = templateState.tee_times
      .filter(teetime => {
        //If selected tab is divisions length, then "All" tab is selected and division id doesn't need to be checked
        const matchesDivision =
          selectedTab === divisions?.length ? true : teetime.division_id === divisions[selectedTab]?.id;

        //Check if the tee time is already selected. Don't add it if its already selected
        const alreadyIncluded = updatedSelectedTeeTimes.includes(teetime?.id);
        const matchesTime = isWithinTimeRange(teetime?.start_time, timeFilter.startTime, timeFilter.endTime);

        return matchesDivision && matchesTime && !alreadyIncluded && !teetime?.shotgun_application_id;
      })
      ?.map(teetime => teetime?.id);
    //Select the tee times within the time range
    updatedSelectedTeeTimes = [...updatedSelectedTeeTimes, ...selectTeeTimeIds];

    ReactDOM.unstable_batchedUpdates(() => {
      setFilterTimes({ startTime: timeFilter?.startTime, endTime: timeFilter?.endTime });
      setTimeFilter(prev => ({ ...prev, open: false }));
      setSettingsState(prev => ({ ...prev, selectedTeetimes: updatedSelectedTeeTimes, drawerOpen: true }));
    });
  }

  async function loadTemplatePage(cancelToken?: CancelToken) {
    dispatch(enqueue());

    try {
      const [bookingCategoryRes, templateRes, shotgunTemplateRes] = await Promise.all([
        GetBookingCategory(null, false, cancelToken),
        GetTeeSheetTemplate({ id: Number(templateId), extended: true }, false, cancelToken),
        GetShotGunTemplate(null, false),
      ]);

      if (bookingCategoryRes.status !== StatusCode.OK) {
        if (cancelToken && cancelToken?.reason) {
          return;
        }
        dispatch(showError(t("Error when loading booking category")));
        return;
      }

      if (templateRes.status !== StatusCode.OK) {
        if (cancelToken && cancelToken?.reason) {
          return;
        }
        dispatch(showError(t("Error when loading template")));
        return;
      }

      if (shotgunTemplateRes?.status !== StatusCode.OK) {
        if (cancelToken && cancelToken?.reason) {
          return;
        }
        dispatch(showError(t("Error getting shotgun templates")));
        return;
      }

      const template = templateRes.data[0];

      const usedLeagueIds: Set<number> = new Set();
      const usedTournamentIds: Set<number> = new Set();

      template?.tee_times?.forEach(teeTime => {
        if (teeTime.league_id) {
          usedLeagueIds.add(teeTime.league_id);
        }
        if (teeTime.tournament_id) {
          usedTournamentIds.add(teeTime.tournament_id);
        }
      });

      const [usedLeaguesRes, usedTournamentsRes] = await Promise.all([
        GetLeague({ ids: Array.from(usedLeagueIds) }, false, cancelToken),
        GetTournament({ ids: Array.from(usedTournamentIds) }, false, cancelToken),
      ]);

      if (usedLeaguesRes.status !== StatusCode.OK) {
        if (cancelToken && cancelToken?.reason) {
          return;
        }
        dispatch(showError(t("Error when loading leagues")));
        return;
      }

      if (usedTournamentsRes.status !== StatusCode.OK) {
        if (cancelToken && cancelToken?.reason) {
          return;
        }
        dispatch(showError(t("Error when loading tournaments")));
        return;
      }

      const teeTimes: ITeeTime[] = template?.tee_times?.map(teeTime => ({
        ...teeTime,
        league:
          teeTime?.league_id != null ? usedLeaguesRes.data.find(league => league?.id === teeTime.league_id) : null,
        tournament:
          teeTime?.tournament_id != null
            ? usedTournamentsRes.data.find(tournament => tournament?.id === teeTime.tournament_id)
            : null,
      }));

      const shotgunMap = buildShotgunGroups(teeTimes);

      // Array of tee times and shotgun groups
      const renderList: TRenderItem[] = buildRenderList(teeTimes, shotgunMap);

      const divisions_list =
        facilityStore?.facility?.courses?.find(course => course?.id === template?.course_id)?.divisions ?? [];

      const teetimeMap: { [key: number]: ITeeTime } = {};
      teeTimes?.forEach(time => {
        teetimeMap[time?.id] = time;
      });

      ReactDOM.unstable_batchedUpdates(() => {
        setDivisions(divisions_list);
        setBookingCategories(bookingCategoryRes.data);
        setTemplateState({ ...template, tee_times: teeTimes });
        setTemplateBeforeState({ ...template, tee_times: teeTimes });
        setEditTemplateState({
          name: template?.name ?? "",
          description: template?.description ?? "",
          edit: false,
        });
        setTeeTimesMap(teetimeMap);
        setRenderList(renderList);
        setRenderListBeforeState(renderList);
      });
    } finally {
      dispatch(dequeue());
    }
  }

  async function saveTemplate() {
    const updatedTeetimes: ITeeTime[] = templateState.tee_times?.filter((teetime, index: number) => {
      return !isEqualWith(teetime, templateBeforeState?.tee_times[index]);
    });

    const updatedTeetimesWithBookingCategories: ITeeTimeBookingCategoryUpdate[] = updatedTeetimes?.map(
      updatedTeetime => {
        return {
          ...updatedTeetime,
          booking_category_ids: updatedTeetime.booking_categories?.map(bookingCategory => bookingCategory.id) ?? [],
        };
      },
    );

    for (let i = 0; i < updatedTeetimesWithBookingCategories.length; i++) {
      if (
        updatedTeetimesWithBookingCategories[i].tournament_id !== null &&
        updatedTeetimesWithBookingCategories[i].league_id !== null
      ) {
        dispatch(showError(t("Teetime cannot have both a league and tournament")));
        return;
      }
    }

    const templateRes = await PutTeeSheetTemplate(
      {
        id: templateState.id,
        name: editTemplateState.name,
        tee_times: updatedTeetimesWithBookingCategories,
        description: editTemplateState.description,
        increment_version: false,
      },
      true,
    );

    if (templateRes.status !== StatusCode.OK) {
      dispatch(showError(typeof templateRes?.data === "string" ? templateRes?.data : t("Error updating template")));
      return;
    }

    //Save completed, increment version when leaving the page
    setSaveMade(true);

    dispatch(showSuccess(t("Template saved successfully")));

    await loadTemplatePage();
  }

  async function incrementVersion() {
    await PutTeeSheetTemplate(
      {
        id: templateState.id,
        name: editTemplateState.name,
        tee_times: [],
        description: editTemplateState.description,
        increment_version: true,
      },
      true,
    );
  }

  const onSelectedTeetimeChange = (e: React.MouseEvent<HTMLDivElement, MouseEvent>, newId?: number) => {
    e.stopPropagation();
    if (newId === undefined || isNaN(newId)) {
      return;
    }

    let newStartTime = settingsState?.startTime ?? "";
    let newEndTime = settingsState?.endTime ?? "";

    if (!shiftPressed) {
      if (settingsState.selectedTeetimes.some(id => id === newId)) {
        const updatedSelections = settingsState.selectedTeetimes
          .filter(teetimeId => {
            if (teetimeId === newId) {
              return false;
            } else {
              return true;
            }
          })
          .map(teetimetId => Number(teetimetId));

        const momentTimes = updatedSelections?.map(time => moment(teeTimesMap[time]?.start_time, "HH:mm"));
        newStartTime = moment.min(momentTimes)?.format("LT");
        newEndTime = moment.max(momentTimes)?.format("LT");

        setSettingsState(prevState => ({
          ...prevState,
          selectedTeetimes: updatedSelections,
          startTime: newStartTime,
          endTime: newEndTime,
        }));
      } else {
        const updatedTimes = settingsState.selectedTeetimes ? [...settingsState.selectedTeetimes, newId] : [newId];
        const momentTimes = updatedTimes?.map(time => moment(teeTimesMap[time]?.start_time, "HH:mm"));
        newStartTime = moment.min(momentTimes)?.format("LT");
        newEndTime = moment.max(momentTimes)?.format("LT");
        setSettingsState(prevState => ({
          ...prevState,
          selectedTeetimes: [...prevState.selectedTeetimes, newId],
          drawerOpen: !prevState?.drawerOpen && !shotgunSettingsState?.drawerOpen ? true : prevState?.drawerOpen,
          startTime: newStartTime,
          endTime: newEndTime,
        }));
      }
    } else {
      if (!settingsState.selectedTeetimes.some(id => id === newId)) {
        const selectedTeetime = templateState?.tee_times?.find(teetime => teetime?.id === newId);
        const divisionTeetimes = templateState?.tee_times.filter(
          teetime => teetime?.division_id === selectedTeetime?.division_id,
        );
        const selectedTeetimeDivisionIndex = divisionTeetimes?.findIndex(
          teetime => teetime?.id === selectedTeetime?.id,
        );

        if (
          selectedTeetime === undefined ||
          divisionTeetimes === undefined ||
          selectedTeetimeDivisionIndex === undefined ||
          selectedTeetimeDivisionIndex === -1
        ) {
          return;
        }

        let previousSelectedDivisionIndex = undefined;

        for (let i = selectedTeetimeDivisionIndex - 1; i >= 0 && previousSelectedDivisionIndex === undefined; i--) {
          if (settingsState?.selectedTeetimes?.some(id => id === divisionTeetimes[i]?.id)) {
            previousSelectedDivisionIndex = i;
          }
        }

        if (previousSelectedDivisionIndex === undefined) {
          return;
        }

        const selectedDivisionIndexes = range(previousSelectedDivisionIndex + 1, selectedTeetimeDivisionIndex + 1);

        const selectedTeetimes = selectedDivisionIndexes.map(index => {
          return divisionTeetimes?.[index]?.id;
        });

        const updatedTimes = settingsState.selectedTeetimes
          ? [...settingsState.selectedTeetimes, ...selectedTeetimes]
          : [...selectedTeetimes];
        const momentTimes = updatedTimes?.map(time => moment(teeTimesMap[time]?.start_time, "HH:mm"));
        newStartTime = moment.min(momentTimes)?.format("LT");
        newEndTime = moment.max(momentTimes)?.format("LT");

        setSettingsState(prevState => ({
          ...prevState,
          selectedTeetimes: [...prevState.selectedTeetimes, ...selectedTeetimes],
          drawerOpen: !prevState?.drawerOpen && !shotgunSettingsState?.drawerOpen ? true : prevState?.drawerOpen,
          startTime: newStartTime,
          endTime: newEndTime,
        }));
      }
    }
  };

  function handleSelectAllDivisonCheckboxChange(
    event: React.ChangeEvent<HTMLInputElement>,
    teetimesByDivisionIds: Array<number>,
  ) {
    const { checked } = event.target;
    let updatedSelectedTimes = settingsState?.selectedTeetimes ? [...settingsState.selectedTeetimes] : [];
    if (!checked) {
      updatedSelectedTimes = updatedSelectedTimes?.filter(teetime => !teetimesByDivisionIds.includes(teetime));
    } else {
      updatedSelectedTimes = updatedSelectedTimes?.filter(teetime => !teetimesByDivisionIds.includes(teetime));
      updatedSelectedTimes = [...updatedSelectedTimes, ...teetimesByDivisionIds];
    }

    const updatedTimes = updatedSelectedTimes;
    const momentTimes = updatedTimes?.map(time => moment(teeTimesMap[time]?.start_time, "HH:mm"));
    const newStartTime = moment.min(momentTimes)?.format("LT");
    const newEndTime = moment.max(momentTimes)?.format("LT");

    setSettingsState(prevState => ({
      ...prevState,
      selectedTeetimes: updatedSelectedTimes,
      drawerOpen: !prevState?.drawerOpen && checked && !shotgunSettingsState?.drawerOpen ? true : prevState?.drawerOpen,
      startTime: newStartTime,
      endTime: newEndTime,
    }));
  }

  function handleSelectAllCheckboxChange(event: React.ChangeEvent<HTMLInputElement>) {
    const { checked } = event.target;
    if (!checked) {
      void unselectAll();
    } else {
      void selectAll();
    }
  }

  function selectAll() {
    const everyTeeTimeId =
      selectedTab !== divisions?.length
        ? templateState.tee_times
            ?.filter(teetime => {
              const matchesDivision = teetime.division_id === divisions[selectedTab].id;
              const matchesTime = isWithinTimeRange(teetime?.start_time, filterTimes.startTime, filterTimes.endTime);
              return matchesDivision && matchesTime && !teetime?.shotgun_application_id;
            })
            .map(teetime => {
              return teetime?.id;
            })
        : templateState?.tee_times
            ?.filter(teetime => {
              const matchesTime = isWithinTimeRange(teetime?.start_time, filterTimes.startTime, filterTimes.endTime);
              return matchesTime && !teetime?.shotgun_application_id;
            })
            .map(teetime => {
              return teetime?.id;
            });
    const updatedTimes = everyTeeTimeId;
    const momentTimes = updatedTimes?.map(time => moment(teeTimesMap[time]?.start_time, "HH:mm"));
    const newStartTime = moment.min(momentTimes)?.format("LT");
    const newEndTime = moment.max(momentTimes)?.format("LT");
    setSettingsState(prevState => ({
      ...prevState,
      selectedTeetimes: everyTeeTimeId,
      drawerOpen: !prevState?.drawerOpen && !shotgunSettingsState?.drawerOpen ? true : prevState?.drawerOpen,
      startTime: newStartTime,
      endTime: newEndTime,
    }));
  }

  function unselectAll() {
    setSettingsState(prevState => ({ ...prevState, selectedTeetimes: [] }));
  }

  function closeDrawer(deSelectTeeTimes?: boolean) {
    ReactDOM.unstable_batchedUpdates(() => {
      setSettingsState(prev => ({
        ...prev,
        selectedTeetimes: deSelectTeeTimes ? [] : prev.selectedTeetimes,
        drawerOpen: false,
      }));
    });
  }

  function applySettings(settings: ITeeTimeSettingsState & { tournament?: ITournament; league?: ILeague }) {
    if (settingsState.selectedTeetimes === undefined || settingsState.selectedTeetimes.length === 0) {
      dispatch(showError(t("Tee times must be selected")));
      return;
    }

    const selectedBookingCategories: IBookingCategory[] = [];
    const updatedSelectedTeetimes: ITeeTime[] = [];

    settings?.booking_category_ids.map(bookingCategoryId => {
      const selectedBookingCategory = bookingCategories?.find(
        bookingCategory => bookingCategory.id === bookingCategoryId,
      );
      if (selectedBookingCategory) {
        selectedBookingCategories.push(selectedBookingCategory);
      }
    });

    settingsState.selectedTeetimes?.map(selectedId => {
      const selectedTeetime = templateState.tee_times?.find(teetime => teetime?.id === selectedId);

      if (selectedTeetime === undefined) {
        return;
      }

      let league_id: number = null;
      let league: ILeague = null;

      if (settings?.league_id === "none") {
        league_id = null;
        league = null;
      } else if (isNaN(parseInt(settings?.league_id))) {
        league_id = selectedTeetime.league_id;
        league = selectedTeetime.league;
      } else {
        league_id = parseInt(settings.league_id);
        league = settings?.league;
      }

      let tournament_id: number = null;
      let tournament: ITournament = null;

      if (settings?.tournament_id === "none") {
        tournament_id = null;
        tournament = null;
      } else if (isNaN(parseInt(settings?.tournament_id))) {
        tournament_id = selectedTeetime.tournament_id;
        tournament = selectedTeetime.tournament;
      } else {
        tournament_id = parseInt(settings.tournament_id);
        tournament = settings?.tournament;
      }

      const updatedSelectedTeetime: ITeeTime = {
        ...selectedTeetime,
        blocked_type: isEmpty(settings?.blocked_type) ? selectedTeetime.blocked_type : settings?.blocked_type,
        cart_rule: isEmpty(settings?.cart_rule) ? selectedTeetime.cart_rule : settings?.cart_rule,
        league_id,
        league,
        tournament_id,
        tournament,
        turn_division_id: isNaN(parseInt(settings?.turn_division_id))
          ? selectedTeetime.turn_division_id
          : parseInt(settings?.turn_division_id),
        pre_paid_required: isEmpty(settings?.pre_paid_required)
          ? selectedTeetime.pre_paid_required
          : settings?.pre_paid_required,
        credit_card_required: isEmpty(settings?.credit_card_required)
          ? selectedTeetime.credit_card_required
          : settings?.credit_card_required,
        divisions_available:
          settings.divisions_available.length === 0
            ? selectedTeetime.divisions_available
            : settings.divisions_available,
        booking_categories:
          settings.booking_category_ids.length === 0 ? selectedTeetime.booking_categories : selectedBookingCategories,
        web_booking_enabled: isEmpty(settings?.web_booking_enabled)
          ? selectedTeetime.web_booking_enabled
          : settings?.web_booking_enabled,
        bookings_prevent_guests: isEmpty(settings?.bookings_prevent_guests)
          ? selectedTeetime?.bookings_prevent_guests
          : settings?.bookings_prevent_guests,
      };

      updatedSelectedTeetimes.push(updatedSelectedTeetime);
    });

    const updatedTeetimes = templateState.tee_times?.map(originalTeetime => {
      const updatedTeetime = updatedSelectedTeetimes.find(updatedTeetime => updatedTeetime?.id === originalTeetime?.id);
      return updatedTeetime ?? originalTeetime;
    });

    const updatedRenderList = renderList?.map(renderItem => {
      if (renderItem?.type === "shotgun") {
        return renderItem;
      } else {
        const updatedTeetime = updatedSelectedTeetimes.find(
          updatedTeetime => updatedTeetime?.id === renderItem?.data?.id,
        );
        return updatedTeetime ? { ...renderItem, data: updatedTeetime } : renderItem;
      }
    });

    for (let i = 0; i < updatedTeetimes.length; i++) {
      if (updatedTeetimes[i].tournament_id != null && updatedTeetimes[i].league_id != null) {
        dispatch(showError(t("Teetime cannot have both a league and tournament")));
        return;
      }
    }

    void closeDrawer(true);
    ReactDOM.unstable_batchedUpdates(() => {
      setTemplateState(prevState => ({ ...prevState, tee_times: updatedTeetimes ?? prevState.tee_times }));
      setRenderList(updatedRenderList);
    });
  }

  function cancelUnsavedChanges() {
    ReactDOM.unstable_batchedUpdates(() => {
      setRenderList([...renderListBeforeState]);
      setTemplateState({ ...templateBeforeState });
    });
  }

  function closeShotgunDrawer() {
    setShotgunSettingsState({
      drawerOpen: false,
    });
  }

  async function applyShotgun(settings: ITeeTimeSettingShotgunState) {
    if (!settings?.startTime) {
      dispatch(showError(t("Start time required")));
      return;
    } else if (!settings?.duration) {
      dispatch(showError(t("Duration required")));
      return;
    } else if (!settings?.leadTime) {
      dispatch(showError(t("Lead time required")));
      return;
    } else if (!settings?.frontDivisionId) {
      dispatch(showError(t("Front division required")));
      return;
    } else if (!settings?.backDivisionId) {
      dispatch(showError(t("Back division required")));
      return;
    } else if (!settings?.shotgunTemplateId) {
      dispatch(showError(t("Template required")));
      return;
    }
    const durationTime = settings?.duration;
    const durationSplit = durationTime.split(":");
    const durationMins = +durationSplit[0] * 60 + +durationSplit[1];
    const leadTime = settings?.leadTime;
    const leadTimeSplit = leadTime.split(":");
    const leadTimeMins = +leadTimeSplit[0] * 60 + +leadTimeSplit[1];

    const res = await PutApplyShotgunTeeSheetTemplate(
      {
        id: Number(templateId),
        shotgun_template_id: settings?.shotgunTemplateId,
        start_time: settings?.startTime,
        duration: durationMins,
        lead_time: leadTimeMins,
        division_id: settings?.frontDivisionId,
        turn_division_id: settings?.backDivisionId,
        league_id: settings?.leagueId,
        tournament_id: settings?.tournamentId,
        booking_category_ids: settings?.bookingCategoryIds,
      },
      true,
    );
    if (res?.status !== StatusCode.OK) {
      dispatch(showError(typeof res?.data === "string" ? res?.data : t("Error applying shotgun to template")));
      return;
    }

    dispatch(showSuccess(t("Successfully applied shotgun to template")));
    void closeShotgunDrawer();
    //Refresh template
    await loadTemplatePage();
  }

  async function deleteShotgun() {
    if (deleteShotgunModal?.teetime?.type === "shotgun" && deleteShotgunModal?.teetime?.data?.applicationId) {
      const res = await PutRemoveShotgunTeeSheetTemplate(
        { id: Number(templateId), shotgun_application_id: deleteShotgunModal?.teetime?.data?.applicationId },
        true,
      );
      if (res?.status !== StatusCode.OK) {
        dispatch(showError(typeof res?.data === "string" ? res?.data : t("Error removing shotgun from template")));
        return;
      }
      dispatch(showSuccess(t("Successfully removed shotgun from template")));
      void closeDeleteShotgunModal();
      // Refresh template
      await loadTemplatePage();
    }
  }

  async function archiveTemplate() {
    const params = {
      id: templateState.id,
    };

    const archiveTemplateRes = await ArchiveTeeSheetTemplate(params, true);

    if (archiveTemplateRes.status !== StatusCode.OK) {
      dispatch(
        showError(
          typeof archiveTemplateRes?.data === "string" ? archiveTemplateRes?.data : t("Error archiving template"),
        ),
      );
      return;
    }

    dispatch(showSuccess(t("Successfully archived template")));
    void closeArchiveModal();
    history.push("/admin/settings/tee-sheet/template");
  }

  async function duplicateTemplate() {
    const params = {
      id: templateState.id,
    };

    const res = await PostDuplicateTemplate(params, true);

    if (res.status !== StatusCode.OK) {
      dispatch(showError(typeof res?.data === "string" ? res?.data : t("Error archiving template")));
      return;
    }

    dispatch(showSuccess(t("Successfully duplicated template")));
    void closeDuplicateModal();
    history.push("/admin/settings/tee-sheet/template");
  }

  async function deleteTemplate() {
    const params = {
      id: templateState.id,
    };

    const res = await DeleteTeeSheetTemplate(params, true);

    if (res.status !== StatusCode.OK) {
      dispatch(showError(typeof res?.data === "string" ? res?.data : t("Error deleting template")));
      return;
    }

    dispatch(showSuccess(t("Successfully deleted template")));
    void closeDeleteModal();
    history.push("/admin/settings/tee-sheet/template");
  }

  // console.log("renderList", renderList);

  function convertTurnTime(teeTimeStartTime: string) {
    const turnTime = Number(templateState?.turn_time ?? 0);

    const [hours, minutes] = teeTimeStartTime.split(":").map(Number);

    const totalMinutes = hours * 60 + minutes + turnTime;

    const newHours = Math.floor(totalMinutes / 60) % 24;
    const newMinutes = totalMinutes % 60;

    const time = `${String(newHours).padStart(2, "0")}:${String(newMinutes).padStart(2, "0")}`;

    return convertTime(time);
  }

  const MemoizedTeetimes: JSX.Element = useMemo(() => {
    if (templateState?.tee_times === undefined || templateState?.tee_times?.length === 0) {
      return <div>{t("There are no tee times for this template")}</div>;
    } else {
      const tabs = divisions?.map(division => {
        return {
          id: `${String(division.title)} - ${String(division.id)}`,
          content: division.title,
        };
      });

      const condensedView = Number(tabs.length);

      tabs.push({
        id: "Condensed View",
        content: "All",
      });

      return (
        <Card
          title={t("Tee times")}
          subtitle={t("Select tee times to apply rules, events, restrictions, or payment requirements.")}
          titleDetails={[
            {
              content: (
                <div className="relative" ref={timeFilterRef}>
                  <Button
                    type="secondary"
                    size="medium"
                    onClick={() =>
                      setTimeFilter(prev => ({
                        ...prev,
                        open: !prev.open,
                        startTime: filterTimes?.startTime ? filterTimes?.startTime : "",
                        endTime: filterTimes?.endTime ? filterTimes?.endTime : "",
                      }))
                    }
                  >
                    <FontAwesomeIcon icon={faClock} className="mr-2" />
                    {!filterTimes?.startTime && !filterTimes?.endTime
                      ? t("Time range")
                      : `${filterTimes?.startTime ? moment(filterTimes?.startTime, "HH:mm")?.format("LT") : ""} ${
                          filterTimes?.startTime && filterTimes?.endTime ? "-" : ""
                        } ${filterTimes?.endTime ? moment(filterTimes?.endTime, "HH:mm")?.format("LT") : ""}`}
                  </Button>
                  <Popover position="left" active={timeFilter.open} overflow>
                    <div className="ui-teesheet-template-time-container">
                      <div className="ui-teesheet-template-time-container-main">
                        <p className="ui-teesheet-template-time-title">{t("Select time range")}</p>
                        <FormLayout>
                          <FormLayout.Group>
                            <TimePick
                              label={t("From")}
                              value={timeFilter?.startTime}
                              onChange={time => setTimeFilter(prev => ({ ...prev, startTime: time }))}
                              size="medium"
                            />
                          </FormLayout.Group>
                          <FormLayout.Group>
                            <TimePick
                              label={t("To")}
                              value={timeFilter?.endTime}
                              onChange={time => setTimeFilter(prev => ({ ...prev, endTime: time }))}
                              size="medium"
                            />
                          </FormLayout.Group>
                        </FormLayout>
                      </div>
                      <div className="ui-teesheet-template-time-container-footer">
                        <Button
                          block
                          size="small"
                          type="secondary"
                          onClick={() =>
                            ReactDOM.unstable_batchedUpdates(() => {
                              setTimeFilter({ open: false, startTime: "", endTime: "" });
                              setFilterTimes({ startTime: "", endTime: "" });
                            })
                          }
                        >
                          {t("Clear")}
                        </Button>
                        <Button block size="small" type="primary" onClick={applyTimeRange}>
                          {t("Apply")}
                        </Button>
                      </div>
                    </div>
                  </Popover>
                </div>
              ),
            },
            {
              content: (
                <Button
                  type="secondary"
                  size="medium"
                  onClick={() => setShotgunSettingsState(prev => ({ ...prev, drawerOpen: true }))}
                  disabled={settingsState?.drawerOpen}
                >
                  <FontAwesomeIcon icon={faPlus} className="mr-2" /> {t("Add shotgun")}
                </Button>
              ),
            },
          ]}
        >
          {settingsState?.selectedTeetimes?.length > 0 ? (
            <div className="flex items-center gap-2" style={{ padding: "16px 24px 0 24px" }}>
              <p className="font-medium" style={{ padding: "8px 18px 8px 0" }}>
                {settingsState?.selectedTeetimes?.length} {t("selected")}
              </p>
              <div className="ui-teesheet-template-divider"></div>
              <Button size="medium" type="text" onClick={selectAll}>
                {t("Select all")}
              </Button>
              <div className="ui-teesheet-template-divider"></div>
              <Button
                size="medium"
                type="text"
                onClick={() => {
                  void unselectAll();
                  void closeDrawer();
                }}
              >
                <FontAwesomeIcon icon={faXmark} className="mr-2" /> {t("Clear")}
              </Button>
              {!settingsState?.drawerOpen ? (
                <>
                  <div className="ui-teesheet-template-divider"></div>
                  <Button
                    size="medium"
                    type="text"
                    onClick={() => setSettingsState(prev => ({ ...prev, drawerOpen: true }))}
                  >
                    {t("Open settings")}
                  </Button>
                </>
              ) : null}
            </div>
          ) : null}
          <Card.Section>
            <Tabs tabs={tabs} selected={selectedTab} onSelect={tab => setSelectedTab(tab)} variant="underline" />
          </Card.Section>
          <div>
            <Card.Section table="true">
              {divisions?.map((division, index: number) => {
                if (selectedTab === index) {
                  return (
                    <div key={index} className="ui-teesheet-template-table-container">
                      <DataTable
                        columns={[
                          {
                            label: t("secure.facility.tee_sheet.tee_sheet_edit.034"),
                            content: (
                              <div className="flex">
                                <Checkbox
                                  id="selectAllTeetimes"
                                  size="medium"
                                  checked={
                                    settingsState?.selectedTeetimes?.filter(time =>
                                      templateState?.tee_times?.some(
                                        t => t.id === time && t.division_id === divisions[index]?.id,
                                      ),
                                    )?.length ===
                                    renderList?.filter(teetime => {
                                      if (teetime?.type === "shotgun") {
                                        return false;
                                      }
                                      return teetime?.data?.division_id === divisions[index]?.id;
                                    })?.length
                                  }
                                  onChange={handleSelectAllCheckboxChange}
                                  labelHidden
                                  indeterminate={
                                    settingsState?.selectedTeetimes?.filter(time =>
                                      templateState?.tee_times?.some(
                                        t => t.id === time && t.division_id === divisions[index]?.id,
                                      ),
                                    )?.length > 0
                                  }
                                />

                                <span className="shotgun-template-edit-label">{t("Time")}</span>
                              </div>
                            ),
                          },
                          { label: t("Turn") },
                          { label: "" },
                          { label: t("Carts") },
                          { label: t("Event") },
                          { label: "" },
                          { label: "" },
                          { label: t("Holes") },
                          { label: t("Booking Categories") },
                          { label: t("Turn") },
                          { label: t("Web Booking") },
                          { label: t("Allow Guests") },
                        ]}
                      >
                        {renderList
                          .filter(teetime => {
                            const teeTime =
                              teetime?.type === "teetime"
                                ? teetime?.data
                                : teetime?.data?.teetimes?.find(time => time?.division_id === division?.id) ??
                                  teetime?.data?.teetimes[0];
                            const matchesDivision = teeTime?.division_id === division?.id;

                            const matchesTime = isWithinTimeRange(
                              teeTime?.start_time,
                              filterTimes.startTime,
                              filterTimes.endTime,
                            );

                            return matchesDivision && matchesTime;
                          })
                          .map((teetime, index: number) => {
                            const teeTime = teetime?.type === "teetime" ? teetime?.data : teetime?.data?.teetimes[0];
                            const divisionsListLabel = divisions?.find(
                              division => division.id === teeTime?.turn_division_id,
                            )?.title;

                            const divisionsAvailableLabels: string[] = [];

                            teeTime?.divisions_available?.forEach(divisionAvailable => {
                              const divisionLabel = divisionOptions?.find(
                                divisionOption => divisionOption?.value === divisionAvailable,
                              )?.label;
                              if (divisionLabel) {
                                divisionsAvailableLabels.push(divisionLabel);
                              }
                            });

                            if (teetime?.type === "teetime") {
                              return (
                                <tr
                                  key={teetime?.data?.id}
                                  className="clickable prevent_select"
                                  onClick={(e: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
                                    onSelectedTeetimeChange(e, teetime?.data?.id);
                                  }}
                                >
                                  <td className="ui-teesheet-template-table-sticky-col ui-teesheet-template-table-time-col">
                                    <Checkbox
                                      id={`teetime ${index}`}
                                      size="medium"
                                      checked={settingsState.selectedTeetimes.some(id => id === teetime?.data?.id)}
                                      label={convertTime(teetime?.data?.start_time)}
                                      readOnly
                                    />
                                  </td>
                                  <td>
                                    <div className="mr-1">
                                      <span>{convertTurnTime(teetime?.data?.start_time)}</span>
                                    </div>
                                  </td>
                                  <td className="ui-teesheet-template-table-sticky-col ui-teesheet-template-table-type-col">
                                    {teetime?.data?.blocked_type === "open" ? (
                                      <Badge type="success">{capitalize(teetime?.data?.blocked_type)}</Badge>
                                    ) : null}

                                    {teetime?.data?.blocked_type === "blocked" ? (
                                      <Badge type="warning">{capitalize(teetime?.data?.blocked_type)}</Badge>
                                    ) : null}
                                    {teetime?.data?.blocked_type === "shotgun_blocked" ||
                                    teetime?.data?.blocked_type === "shotgun_crossover_blocked" ? (
                                      <Badge type="warning">{capitalize("blocked")}</Badge>
                                    ) : null}
                                    {teetime?.data?.blocked_type === "crossover" ? (
                                      <Badge type="gray">{t("Turn")}</Badge>
                                    ) : null}
                                    {teetime?.data?.blocked_type === "shotgun" ? (
                                      <Badge type="gray">{teetime?.data?.start_hole}</Badge>
                                    ) : null}
                                  </td>
                                  <td>
                                    <Badge type="gray">{capitalize(teetime?.data?.cart_rule)}</Badge>
                                  </td>
                                  <td>
                                    {teetime?.data?.tournament ? (
                                      <Badge type="gray" iconLeft icon={faTrophy}>
                                        {teetime?.data?.tournament.name}
                                      </Badge>
                                    ) : null}
                                    {teetime?.data?.league ? (
                                      <Badge type="gray" iconLeft icon={faUsers}>
                                        {teetime?.data?.league.name}
                                      </Badge>
                                    ) : null}
                                  </td>
                                  <td>
                                    {teetime?.data?.pre_paid_required ? (
                                      <Badge type="warning" iconLeft icon={faDollarSign}>
                                        {t("secure.facility.tee_sheet.tee_sheet_edit.042")}
                                      </Badge>
                                    ) : null}
                                  </td>
                                  <td>
                                    {teetime?.data?.credit_card_required ? (
                                      <Badge type="error" iconLeft icon={faCreditCardFront}>
                                        {t("secure.facility.tee_sheet.tee_sheet_edit.043")}
                                      </Badge>
                                    ) : null}
                                  </td>
                                  <td>
                                    {divisionsAvailableLabels.length > 0 ? (
                                      <span>{divisionsAvailableLabels?.join(", ")}</span>
                                    ) : null}
                                  </td>
                                  <td>
                                    {teetime?.data?.booking_categories &&
                                    teetime?.data?.booking_categories.length > 0 ? (
                                      <span>
                                        {teetime?.data?.booking_categories
                                          .map(bookingCategory => bookingCategory.title)
                                          .sort()
                                          ?.join(", ")}
                                      </span>
                                    ) : null}
                                  </td>
                                  <td>
                                    {teetime?.data?.turn_division_id && divisionsListLabel ? (
                                      <span>
                                        <FontAwesomeIcon className="mr-2" icon={["far", "arrow-right"]} />
                                        {divisionsListLabel}
                                      </span>
                                    ) : null}
                                  </td>
                                  <td>
                                    {teetime?.data?.web_booking_enabled ? (
                                      <span>
                                        <FontAwesomeIcon className="mr-2 " icon={["far", "globe"]} />
                                      </span>
                                    ) : null}
                                  </td>
                                  <td>
                                    {!teetime?.data?.bookings_prevent_guests ? (
                                      <span>
                                        <FontAwesomeIcon className="mr-2" icon={faCheck} />
                                      </span>
                                    ) : (
                                      <span>
                                        <FontAwesomeIcon className="mr-2" icon={faX} />
                                      </span>
                                    )}
                                  </td>
                                </tr>
                              );
                            } else {
                              return (
                                <tr key={teeTime?.id}>
                                  <td colSpan={11} className="ui-teesheet-template-shotgun-cell">
                                    <div className="flex items-center gap-2 justify-between">
                                      <div className="flex items-center gap-2">
                                        <Badge colour="#175CD3" backgroundColour="#EFF8FF" size="medium">
                                          {t("Shotgun start")}
                                        </Badge>
                                        <p className="ui-teesheet-template-shotgun-primary">
                                          {moment(teetime?.data?.startTime, "HH:mm")?.format("LT")} -{" "}
                                          {moment(teetime?.data?.endTime, "HH:mm")?.format("LT")}
                                        </p>
                                        <div className="flex items-center gap-1">
                                          {teeTime?.booking_categories?.map(category => {
                                            const categoryLabel = category?.title[0]?.toLocaleLowerCase();
                                            return (
                                              <OverlayTrigger
                                                placement="top"
                                                overlay={
                                                  <Tooltip id={`category-${category.title}`}>{category.title}</Tooltip>
                                                }
                                                key={category?.id}
                                              >
                                                <FontAwesomeIcon
                                                  icon={["far", `circle-${categoryLabel}` as IconName]}
                                                  className="booking-category-icon"
                                                />
                                              </OverlayTrigger>
                                            );
                                          })}
                                        </div>
                                        <p className="ui-teesheet-template-shotgun-secondary">
                                          {
                                            teetime?.data?.teetimes?.filter(time => time?.division_id === division?.id)
                                              ?.length
                                          }{" "}
                                          {t("tee times are collapsed into this block")}
                                        </p>
                                      </div>
                                      <Button
                                        size="small"
                                        type="error-outline"
                                        onClick={() => updateDeleteShotgunModal({ isOpen: true, teetime })}
                                      >
                                        {t("Remove")}
                                      </Button>
                                    </div>
                                  </td>
                                </tr>
                              );
                            }
                          })}
                      </DataTable>
                    </div>
                  );
                }

                if (selectedTab === condensedView && selectedTab === index + 1) {
                  return (
                    <div key={index} className="flex justify-between w-full gap-2">
                      {filteredTeeTimes.map((teetimeArray: any, index: number) => {
                        const teeTime =
                          teetimeArray[index]?.type === "teetime"
                            ? teetimeArray[index]?.data
                            : teetimeArray[index]?.data?.teetimes[0];
                        return (
                          <div key={index} className="w-full items-center">
                            <div className="ui-teesheet-template-division-title">{teeTime?.division?.title}</div>
                            <div className="ui-teesheet-template-table-container">
                              <DataTable
                                columns={[
                                  {
                                    label: t("Time"),
                                    content: (
                                      <div className="flex">
                                        <Checkbox
                                          id="selectAllTeetimes"
                                          size="medium"
                                          checked={
                                            settingsState?.selectedTeetimes?.filter(time =>
                                              teetimeArray?.some((t: TRenderItem) =>
                                                t?.type === "teetime" ? t?.data?.id === time : false,
                                              ),
                                            )?.length ===
                                            teetimeArray?.filter((time: TRenderItem) => time?.type === "teetime")
                                              ?.length
                                          }
                                          onChange={e =>
                                            handleSelectAllDivisonCheckboxChange(
                                              e,
                                              teetimeArray
                                                ?.filter((teetime: TRenderItem) => {
                                                  if (teetime?.type === "shotgun") {
                                                    return false;
                                                  }
                                                  const matchesTime = isWithinTimeRange(
                                                    teetime?.data?.start_time,
                                                    filterTimes.startTime,
                                                    filterTimes.endTime,
                                                  );

                                                  return matchesTime;
                                                })
                                                ?.map((time: TRenderItem) =>
                                                  time?.type === "teetime" ? time?.data?.id : null,
                                                ) as Array<number>,
                                            )
                                          }
                                          labelHidden
                                          indeterminate={
                                            settingsState?.selectedTeetimes?.filter(time =>
                                              teetimeArray?.some((t: TRenderItem) =>
                                                t?.type === "teetime" ? t?.data?.id === time : false,
                                              ),
                                            )?.length > 0
                                          }
                                        />

                                        <span className="shotgun-template-edit-label">{t("Time")}</span>
                                      </div>
                                    ),
                                  },
                                  { label: "" },
                                  { label: t("secure.facility.tee_sheet.tee_sheet_edit.039") },
                                ]}
                              >
                                {teetimeArray
                                  ?.filter((teetime: TRenderItem) => {
                                    const teeTime =
                                      teetime?.type === "teetime" ? teetime?.data : teetime?.data?.teetimes[0];
                                    const matchesTime = isWithinTimeRange(
                                      teeTime?.start_time,
                                      filterTimes.startTime,
                                      filterTimes.endTime,
                                    );

                                    return matchesTime;
                                  })
                                  ?.map((teetime: TRenderItem) => {
                                    const teeTime =
                                      teetime?.type === "teetime" ? teetime?.data : teetime?.data?.teetimes[0];
                                    const divisionsListLabel = divisions?.find(
                                      (division: any) => division.id === teeTime?.turn_division_id,
                                    )?.title;

                                    const divisionsAvailableLabels: string[] = [];

                                    teeTime?.divisions_available?.forEach((divisionAvailable: number) => {
                                      const divisionLabel = divisionOptions?.find(
                                        divisionOption => divisionOption?.value === divisionAvailable,
                                      )?.label;
                                      if (divisionLabel) {
                                        divisionsAvailableLabels.push(divisionLabel);
                                      }
                                    });
                                    if (teetime?.type === "teetime") {
                                      return (
                                        <tr
                                          key={teetime?.data?.id}
                                          className="clickable prevent_select"
                                          onClick={(e: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
                                            e.stopPropagation();
                                            onSelectedTeetimeChange(e, Number(teetime?.data?.id));
                                          }}
                                        >
                                          <td className="ui-teesheet-template-table-sticky-col ui-teesheet-template-table-time-col">
                                            <Checkbox
                                              id={`teetime ${index}`}
                                              size="medium"
                                              checked={settingsState.selectedTeetimes.some(
                                                id => id === teetime?.data?.id,
                                              )}
                                              label={convertTime(teetime?.data?.start_time)}
                                              readOnly
                                            />
                                          </td>
                                          <td className="ui-teesheet-template-table-sticky-col ui-teesheet-template-table-type-col">
                                            {teetime?.data?.blocked_type === "open" ? (
                                              <Badge type="success">
                                                {capitalize(String(teetime?.data?.blocked_type))}
                                              </Badge>
                                            ) : null}

                                            {teetime?.data?.blocked_type === "blocked" ? (
                                              <Badge type="warning">
                                                {capitalize(String(teetime?.data?.blocked_type))}
                                              </Badge>
                                            ) : null}
                                            {teetime?.data?.blocked_type === "shotgun_blocked" ||
                                            teetime?.data?.blocked_type === "shotgun_crossover_blocked" ? (
                                              <Badge type="warning">{capitalize("blocked")}</Badge>
                                            ) : null}
                                            {teetime?.data?.blocked_type === "crossover" ? (
                                              <Badge type="gray">{t("Turn")}</Badge>
                                            ) : null}
                                          </td>
                                          <td>
                                            {teetime?.data?.turn_division_id && divisionsListLabel ? (
                                              <span>
                                                <FontAwesomeIcon className="mr-2" icon={["far", "arrow-right"]} />
                                                {divisionsListLabel}
                                              </span>
                                            ) : null}
                                          </td>
                                        </tr>
                                      );
                                    } else {
                                      return (
                                        <tr key={teeTime?.id}>
                                          <td colSpan={11} className="ui-teesheet-template-shotgun-cell">
                                            <div className="flex items-center gap-2 justify-between">
                                              <div className="flex items-center gap-2">
                                                <Badge colour="#175CD3" backgroundColour="#EFF8FF" size="medium">
                                                  {t("Shotgun start")}
                                                </Badge>
                                                <p className="ui-teesheet-template-shotgun-primary">
                                                  {moment(teetime?.data?.startTime, "HH:mm")?.format("LT")} -{" "}
                                                  {moment(teetime?.data?.endTime, "HH:mm")?.format("LT")}
                                                </p>
                                                <div className="flex items-center gap-1">
                                                  {teeTime?.booking_categories?.map(category => {
                                                    const categoryLabel = category?.title[0]?.toLocaleLowerCase();
                                                    return (
                                                      <OverlayTrigger
                                                        placement="top"
                                                        overlay={
                                                          <Tooltip id={`category-${category.title}`}>
                                                            {category.title}
                                                          </Tooltip>
                                                        }
                                                        key={category?.id}
                                                      >
                                                        <FontAwesomeIcon
                                                          icon={["far", `circle-${categoryLabel}` as IconName]}
                                                          className="booking-category-icon"
                                                        />
                                                      </OverlayTrigger>
                                                    );
                                                  })}
                                                </div>
                                                <p className="ui-teesheet-template-shotgun-secondary">
                                                  {teetime?.data?.teetimes?.length}{" "}
                                                  {t("tee times are collapsed into this block")}
                                                </p>
                                              </div>
                                              <Button
                                                size="small"
                                                type="error-outline"
                                                onClick={() => updateDeleteShotgunModal({ isOpen: true, teetime })}
                                              >
                                                {t("Remove")}
                                              </Button>
                                            </div>
                                          </td>
                                        </tr>
                                      );
                                    }
                                  })}
                              </DataTable>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  );
                }
              })}
            </Card.Section>
          </div>
        </Card>
      );
    }
  }, [
    divisions,
    templateState?.tee_times,
    settingsState?.selectedTeetimes,
    shiftPressed,
    selectedTab,
    settingsState?.drawerOpen,
    timeFilter,
    filterTimes,
    filteredTeeTimes,
  ]);

  return (
    <div
      style={
        settingsState?.drawerOpen || shotgunSettingsState?.drawerOpen ? { width: "calc(100vw - 782px)" } : undefined
      }
    >
      <Page
        title={templateState?.archived ? templateState?.name + " - Archived" : templateState?.name}
        breadcrumbs={[
          {
            prefix: true,
            label: t("Back to Templates"),
            url: "/admin/settings/tee-sheet/template",
          },
        ]}
        notificationBarProps={{
          isVisible: unsavedChangesExist,
          onAction: () => saveTemplate(),
          onCancel: () => cancelUnsavedChanges(),
        }}
        primaryAction={{
          content: t("Save changes"),
          action: () => saveTemplate(),
        }}
        textAction={{
          content: t("Reset to original"),
          action: () => cancelUnsavedChanges(),
          icon: faArrowUTurnUpLeft,
          hidden: !unsavedChangesExist,
        }}
        multipleActionDropdownAction={{
          label: t("Options"),
          dropdownProps: {
            alignment: "right",
            options: [
              {
                type: "handler",
                label: t("Duplicate template"),
                handler: () => updateDuplicateModal({ isOpen: true }),
                icon: "copy",
                disabled: templateState?.archived,
              },
              {
                type: "handler",
                label: t("Archive template"),
                handler: () => updateArchiveModal({ isOpen: true }),
                icon: "box-archive",
                disabled: templateState?.archived,
              },
              ...(templateState?.archived
                ? [
                    {
                      type: "handler",
                      label: t("Delete template"),
                      handler: () => updateDeleteModal({ isOpen: true }),
                      icon: "trash-can",
                    } as any,
                  ]
                : []),
            ],
          },
        }}
      >
        <Card>
          <Card.Section>
            {!editTemplateState?.edit ? (
              <div className="flex items-center justify-between">
                <div>
                  <p className="ui-teesheet-template-title">{templateState?.name}</p>
                  <p className="ui-teesheet-template-description">{templateState?.description}</p>
                </div>
                <Button
                  size="medium"
                  type="secondary"
                  onClick={() => setEditTemplateState(prev => ({ ...prev, edit: true }))}
                >
                  <FontAwesomeIcon icon={faPen} />
                </Button>
              </div>
            ) : (
              <>
                <FormLayout>
                  <FormLayout.Group>
                    <Input
                      value={editTemplateState?.name ?? ""}
                      label={t("Name")}
                      id="name"
                      onChange={e => setEditTemplateState(prev => ({ ...prev, name: e.target.value }))}
                      placeholder={t("Name")}
                    />
                  </FormLayout.Group>

                  <FormLayout.Group>
                    <TextField
                      value={editTemplateState?.description ?? ""}
                      label={t("Description")}
                      id="description"
                      onChange={e => setEditTemplateState(prev => ({ ...prev, description: e.target.value }))}
                      placeholder={t("Template description")}
                    />
                  </FormLayout.Group>
                </FormLayout>
                <div className="w-100 flex justify-end items-center gap-2">
                  <Button
                    size="medium"
                    type="secondary"
                    onClick={() =>
                      setEditTemplateState(prev => ({
                        ...prev,
                        edit: false,
                        name: templateState?.name ?? "",
                        description: templateState?.description ?? "",
                      }))
                    }
                  >
                    {t("Cancel")}
                  </Button>
                  <Button size="medium" type="primary" onClick={saveTemplate}>
                    {t("Save changes")}
                  </Button>
                </div>
              </>
            )}
          </Card.Section>
        </Card>

        {templateState ? MemoizedTeetimes : null}

        {/* Drawer for editing tee times */}
        <EditTeeTimeDrawer
          open={settingsState?.drawerOpen}
          facility={facilityStore?.facility}
          courseId={templateState?.course_id}
          divisions={divisions}
          bookingCategories={bookingCategories}
          onOk={settings => applySettings(settings)}
          onClose={() => closeDrawer(false)}
          subtitle={
            settingsState?.selectedTeetimes?.length <= 4
              ? `${t("Changes will apply to")} ${settingsState?.selectedTeetimes
                  ?.map(time => moment(teeTimesMap[time]?.start_time, "HH:mm")?.format("LT"))
                  ?.join(", ")}`
              : `${t("Changes will apply to")} ${settingsState?.startTime} ${
                  settingsState?.startTime !== settingsState?.endTime ? ` - ${settingsState?.endTime}` : ""
                }`
          }
        />

        {/* Shotgun drawer */}
        <ShotgunDrawer
          open={shotgunSettingsState?.drawerOpen}
          divisions={divisions}
          bookingCategories={bookingCategories}
          onOk={settings => applyShotgun(settings)}
          onClose={() => closeShotgunDrawer()}
          subtitle={t("Create a shotgun event and apply it to the current tee sheet template.")}
        />

        <Portal isMounted={unsavedModal?.isOpen}>
          <Popup
            open={unsavedModal?.isOpen}
            title={t("Unsaved changes")}
            type="warning"
            description={t("Do you want to go back without saving the changes? Going back will remove them.")}
            onCancel={closeUnsavedModal}
            okText={t("Continue")}
            onOk={() => {
              if (unsavedModal?.location && unsavedModal?.location?.pathname) {
                const pathname = unsavedModal?.location?.pathname;
                void closeUnsavedModal();
                if (unblockRef.current) {
                  unblockRef.current();
                }
                history.push(pathname as string);
              }
            }}
          />
        </Portal>

        <Portal isMounted={deleteShotgunModal?.isOpen}>
          <Popup
            open={deleteShotgunModal?.isOpen}
            title={t("Delete Shotgun")}
            type="warning"
            description={
              deleteShotgunModal?.teetime?.type === "shotgun" &&
              deleteShotgunModal?.teetime?.data?.startTime &&
              deleteShotgunModal?.teetime?.data?.endTime
                ? t("Are you sure you want to delete the shotgun applied from {{shotgunRange}}?", {
                    shotgunRange:
                      deleteShotgunModal?.teetime?.type === "shotgun"
                        ? `${moment(deleteShotgunModal?.teetime?.data?.startTime, "HH:mm")?.format("LT")} - ${moment(
                            deleteShotgunModal?.teetime?.data?.endTime,
                            "HH:mm",
                          )?.format("LT")}`
                        : "",
                  })
                : t("Are you sure you want to delete this shotgun?")
            }
            onCancel={closeDeleteShotgunModal}
            okText={t("Delete")}
            onOk={deleteShotgun}
          />
        </Portal>

        <Portal isMounted={archiveModal.isOpen}>
          <Popup
            open={archiveModal.isOpen}
            type="warning"
            title={t("Archive Template")}
            description={t("Are you sure you want to archive this template?")}
            onOk={() => archiveTemplate()}
            okText={t("Archive")}
            onCancel={closeArchiveModal}
            backDropCancel={true}
          />
        </Portal>

        <Portal isMounted={duplicateModal.isOpen}>
          <Popup
            open={duplicateModal.isOpen}
            type="warning"
            title={t("Duplicate Template")}
            description={t("Are you sure you want to duplicate this template?")}
            onOk={() => duplicateTemplate()}
            okText={t("Duplicate")}
            onCancel={closeDuplicateModal}
            backDropCancel={true}
          />
        </Portal>

        <Portal isMounted={deleteModal?.isOpen}>
          <Popup
            open={deleteModal?.isOpen}
            type="warning"
            title={t("Delete Template")}
            description={t("Are you sure you want to delete this template?")}
            onOk={() => deleteTemplate()}
            okText={t("Delete")}
            onCancel={closeDeleteModal}
            backDropCancel={true}
          />
        </Portal>
      </Page>
    </div>
  );
}
